import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    AcademicCapIcon,
    BeakerIcon,
    UserGroupIcon,
    StarIcon,
    MapPinIcon,
    PresentationChartLineIcon,
    GlobeAltIcon,
    ShieldCheckIcon,
    BuildingOfficeIcon,
    TruckIcon,
    CpuChipIcon,
    CogIcon,
    BriefcaseIcon
} from '@heroicons/react/24/solid';
import SEO from '../../components/SEO';

const Projects = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15
            }
        }
    };

    const oneStopSolutions = [
        {
            id: 1,
            title: "Technology & Intelligence",
            icon: <CpuChipIcon className="w-8 h-8 text-white" />,
            items: ["Satellite-based crop monitoring", "Precision advisory systems", "Data-driven farm management", "Smart analytics for yield optimization"],
            color: "bg-blue-600"
        },
        {
            id: 2,
            title: "Agri Inputs & Infrastructure",
            icon: <BuildingOfficeIcon className="w-8 h-8 text-white" />,
            items: ["High-quality input materials", "Polyhouse & greenhouse solutions", "Net house systems", "Drip irrigation & micro-irrigation solutions", "Hydroponics setup & consultancy"],
            color: "bg-green-600"
        },
        {
            id: 3,
            title: "Landscaping & Gardening",
            icon: <GlobeAltIcon className="w-8 h-8 text-white" />, // Placeholder icon
            items: ["Commercial landscaping projects", "Nursery development", "Urban gardening systems", "Sustainable green infrastructure"],
            color: "bg-emerald-600"
        },
        {
            id: 4,
            title: "Procurement & Market Linkage",
            icon: <TruckIcon className="w-8 h-8 text-white" />,
            items: ["Crop procurement support", "Structured buyer networks", "Export facilitation partnerships"],
            color: "bg-amber-600"
        },
        {
            id: 5,
            title: "Value Addition & Processing",
            icon: <CogIcon className="w-8 h-8 text-white" />,
            items: ["Post-harvest management systems", "Processing unit setup support", "Agri-based product development", "Supply chain integration"],
            color: "bg-purple-600"
        }
    ];

    const whyCollaborate = [
        { title: "Integrated Model", desc: "One partner. Complete agricultural ecosystem.", icon: <UserGroupIcon className="w-6 h-6" /> },
        { title: "Scalable Global Expansion", desc: "We collaborate country-wise through structured business models.", icon: <GlobeAltIcon className="w-6 h-6" /> },
        { title: "Secure & Professional Framework", desc: "Clear agreements. IP protection. Transparent operations.", icon: <ShieldCheckIcon className="w-6 h-6" /> },
        { title: "Long-Term Strategic Partnerships", desc: "We build sustainable alliances — not temporary deals.", icon: <BriefcaseIcon className="w-6 h-6" /> }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <SEO
                title="Farmer Projects & Collaboration - Contract Farming"
                description="Collaborate with Marvelous Agri Solution for contract farming, landscaping, and value-added agricultural projects."
                keywords="Farmer Collaboration, Contract Farming, Agricultural Projects, Landscaping Services, Agri Partnership"
            />
            {/* Hero Section */}
            <section className="relative h-[50vh] md:h-[60vh] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/projects_hero_premium.jpg"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1595831551608-2510b642e12e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1766&q=80' }}
                        alt="Community Impact"
                        className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-black/50 z-10"></div>
                </div>

                <div className="relative z-20 text-center px-4">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-3xl md:text-7xl font-bold mb-4 md:mb-6"
                    >
                        Farmer <span className="text-green-400">Programs</span> & <span className="text-blue-400">Collaboration</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-base md:text-xl text-gray-200 font-light max-w-2xl mx-auto"
                    >
                        Building the Future of Agriculture — Together.
                    </motion.p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">

                {/* --- NEW COLLABORATION CONTENT --- */}

                {/* 1. Collaboration Intro */}
                <div className="text-center mb-20 relative">
                    <span className="text-blue-600 font-bold tracking-widest uppercase text-sm mb-4 block">COLLABORATION</span>
                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8">Let’s Build the Future of <br /><span className="text-blue-600">Agriculture — Together</span></h2>

                    <div className="mb-10 max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-2xl">
                        <img src={require('../../assets/newimages/mas website collboration.webp')} alt="Collaboration" className="w-full h-auto object-cover max-h-[400px]" />
                    </div>

                    <div className="max-w-3xl mx-auto space-y-6 text-lg text-gray-600 leading-relaxed mb-10">
                        <p>
                            Marvelous Agri Solution is a private agri-tech and agri-infrastructure company.
                            We are not a government agency.
                            We operate independently, strategically, and globally.
                        </p>
                        <div className="p-8 bg-white border border-blue-100 shadow-xl rounded-2xl inline-block text-center relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-20 h-20 bg-blue-100 rounded-bl-full -mr-10 -mt-10 opacity-50"></div>
                            <p className="text-lg font-medium text-gray-700 mb-2">We believe agriculture needs more than fragmented services.</p>
                            <p className="text-2xl font-bold text-blue-700 mb-2">It needs an integrated ecosystem.</p>
                            <p className="text-lg font-medium text-gray-900">And that is exactly what we provide.</p>
                        </div>
                    </div>
                </div>

                {/* 2. One-Stop Solution Grid */}
                <div className="mb-24">
                    <div className="text-center mb-16">
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">A TRUE ONE-STOP AGRICULTURE SOLUTION</h3>
                        <p className="text-gray-500 text-lg max-w-2xl mx-auto">From seed to market. From soil to satellite. From production to processing.</p>
                        <p className="text-blue-600 font-semibold mt-2">We deliver complete agricultural solutions under one platform.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {oneStopSolutions.map((sol) => (
                            <div key={sol.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-2xl transition-shadow duration-300 flex flex-col">
                                <div className={`${sol.color} p-6 flex items-center gap-4`}>
                                    <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                        {sol.icon}
                                    </div>
                                    <div className="text-white font-bold text-xl leading-tight opacity-90">{sol.id}. {sol.title}</div>
                                </div>
                                <div className="p-8 flex-grow">
                                    <ul className="space-y-3">
                                        {sol.items.map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-3 text-gray-600">
                                                <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${sol.color.replace('bg-', 'bg-')}`}></div>
                                                <span className="text-sm font-medium">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 3. Why Collaborate */}
                <div className="bg-gray-900 text-white rounded-3xl p-8 md:p-16 mb-24 relative overflow-hidden">
                    {/* Decor */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-600/20 rounded-full blur-3xl -ml-32 -mb-32"></div>

                    <div className="relative z-10">
                        <div className="text-center mb-12">
                            <span className="text-blue-400 font-bold tracking-widest uppercase text-sm mb-2 block">Why Collaborate With Us?</span>
                            <h3 className="text-3xl md:text-4xl font-bold">Building Sustainable Alliances</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                            {whyCollaborate.map((item, idx) => (
                                <div key={idx} className="flex gap-4 items-start bg-white/5 p-6 rounded-2xl border border-white/10 hover:bg-white/10 transition">
                                    <div className="p-3 bg-blue-600 rounded-lg text-white flex-shrink-0">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold text-white mb-2">{item.title}</h4>
                                        <p className="text-gray-300">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 4. Collaboration Opportunities */}
                <div className="mb-24 text-center">
                    <h3 className="text-3xl font-bold text-gray-900 mb-8">Collaboration Opportunities</h3>
                    <p className="mb-10 text-gray-600 text-lg">We invite strategic partners across the ecosystem:</p>

                    <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-16 max-w-5xl mx-auto">
                        {[
                            "International Agri Companies",
                            "Private Distributors",
                            "Infrastructure Developers",
                            "Technology Integrators",
                            "Satellite Data Companies",
                            "Agri Input Brands",
                            "Food Processing Units",
                            "Strategic Business Partners"
                        ].map((tag, idx) => (
                            <span key={idx} className="px-6 py-3 bg-white text-gray-700 font-medium rounded-full shadow-sm border border-gray-200 hover:border-blue-500 hover:text-blue-600 transition-colors cursor-default">
                                {tag}
                            </span>
                        ))}
                    </div>

                    <div className="bg-gradient-to-r from-blue-900 to-green-900 text-white p-10 md:p-14 rounded-3xl max-w-4xl mx-auto shadow-2xl">
                        <h4 className="text-2xl md:text-3xl font-bold mb-4">If you want to enter agriculture with strength, structure, and scale —</h4>
                        <p className="text-xl md:text-2xl text-blue-200 font-medium">we are your execution partner.</p>
                    </div>
                </div>

                {/* 5. Closing Strong Line */}
                <div className="text-center mb-32 border-t border-gray-100 pt-20">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-2">From Innovation to Infrastructure.</h2>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-green-600 mb-6">From Farm to Food.</h2>
                        <p className="text-gray-500 text-xl tracking-wide uppercase font-bold">We Deliver Complete Agricultural Solutions.</p>
                    </motion.div>
                </div>

                {/* --- END NEW CONTENT --- */}

                <div className="text-center mb-16">
                    <span className="bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase mb-4 inline-block">Community Impact</span>
                    <h2 className="text-3xl md:text-5xl font-bold text-gray-900">Farmer Programs</h2>
                </div>

                {/* Programs Grid */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={staggerContainer}
                    className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24"
                >
                    {[
                        {
                            title: "Farmer Education Initiatives",
                            points: ["Awareness programs", "Modern farming knowledge sharing", "Sustainable practices promotion"],
                            icon: <AcademicCapIcon className="w-10 h-10" />,
                            color: "bg-blue-50 text-blue-600"
                        },
                        {
                            title: "On-Field Demonstrations",
                            points: ["Live demo plots", "Technology validation", "Farmer confidence building"],
                            icon: <NavigationIconWrapper />, // Custom wrapper or icon
                            color: "bg-green-50 text-green-600"
                        },
                        {
                            title: "Training & Workshops",
                            points: ["Crop-specific training", "Input application methods", "Digital farming literacy"],
                            icon: <PresentationChartLineIcon className="w-10 h-10" />,
                            color: "bg-orange-50 text-orange-600"
                        },
                    ].map((prog, idx) => (
                        <div key={idx} className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl transition-shadow">
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${prog.color}`}>
                                {prog.icon}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">{prog.title}</h3>
                            <ul className="space-y-3">
                                {prog.points.map((pt, i) => (
                                    <li key={i} className="flex items-start gap-2 text-gray-600">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2"></div>
                                        <span>{pt}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </motion.div>

                {/* Farmer Success Stories */}
                <div className="bg-white rounded-3xl md:rounded-[3rem] p-6 md:p-20 shadow-2xl mb-24 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-400 to-emerald-600"></div>
                    <div className="text-center mb-16">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm">Real Results</span>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-2">Farmer Success Stories</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <motion.div variants={fadeInUp} className="flex gap-6 items-start">
                            <div className="w-20 h-20 bg-gray-200 rounded-full flex-shrink-0 overflow-hidden">
                                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Farmer" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <p className="text-xl text-gray-700 italic mb-4">"Implementing the crop advisory increased my wheat yield by 20% compared to last season. The expert guidance was timely and precise."</p>
                                <h4 className="font-bold text-gray-900">Rajesh Kumar</h4>
                                <p className="text-sm text-green-600 font-bold">Yield Improvement</p>
                            </div>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="flex gap-6 items-start">
                            <div className="w-20 h-20 bg-gray-200 rounded-full flex-shrink-0 overflow-hidden">
                                <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Farmer" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <p className="text-xl text-gray-700 italic mb-4">"The training on water optimization helped me save 40% water while maintaining crop quality. Truly sustainable solutions."</p>
                                <h4 className="font-bold text-gray-900">Vikram Singh</h4>
                                <p className="text-sm text-green-600 font-bold">Cost Optimization</p>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Impact Stats */}
                <motion.section
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    className="text-center"
                >
                    <h2 className="text-3xl font-bold text-gray-900 mb-12">Total Impact</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { label: "Farmers Connected", icon: <UserGroupIcon className="w-8 h-8 text-green-500 mb-2 mx-auto" /> },
                            { label: "Crops Supported", icon: <BeakerIcon className="w-8 h-8 text-green-500 mb-2 mx-auto" /> }, // Placeholder icon
                            { label: "Regions Covered", icon: <MapPinIcon className="w-8 h-8 text-green-500 mb-2 mx-auto" /> },
                            { label: "Sustainability", icon: <StarIcon className="w-8 h-8 text-green-500 mb-2 mx-auto" /> }
                        ].map((item, idx) => (
                            <div key={idx} className="p-6 bg-white rounded-xl shadow-sm">
                                {item.icon}
                                <div className="font-bold text-gray-900">{item.label}</div>
                                <div className="text-sm text-gray-500 mt-1">Growing Every Day</div>
                            </div>
                        ))}
                    </div>
                </motion.section>

                {/* CTA */}
                <section className="mt-16 bg-transparent">
                    <div className="container mx-auto max-w-4xl">
                        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-white text-center">
                            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Farm?</h3>
                            <p className="text-green-100 mb-6">Get in touch with our experts today</p>
                            <Link to="/contact" className="inline-block px-8 py-3 bg-white text-green-700 font-bold rounded-lg hover:bg-gray-100 transition">
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

// Quick helper for missing icon
const NavigationIconWrapper = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10">
        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.75 3c1.99 0 3.757.98 4.75 2.5a5.5 5.5 0 014.75-2.5c3.036 0 5.5 2.322 5.5 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.47 0l-.003-.001z" />
    </svg>
);

export default Projects;
